import type { SearchIndexApp } from '@/lib/search-data'

/**
 * Parses a "vs" query to extract app names
 * Handles variations like:
 * - "notion vs motion"
 * - "notion versus motion"
 * - "notion vs motion vs asana"
 * - "compare notion and motion"
 */
export function parseVsQuery(query: string): string[] | null {
  const normalizedQuery = query.toLowerCase().trim()

  // Check for "compare X and Y" pattern
  const compareMatch = normalizedQuery.match(/^compare\s+(.+)$/i)
  if (compareMatch) {
    const appsString = compareMatch[1]
    // Split by "and", "vs", "versus", or ","
    const apps = appsString
      .split(/\s+(?:and|vs|versus|,)\s+/i)
      .map((s) => s.trim())
      .filter(Boolean)
    if (apps.length >= 2) {
      return apps
    }
  }

  // Check for "X vs Y" or "X versus Y" pattern
  const vsMatch = normalizedQuery.match(/(.+?)\s+(?:vs|versus)\s+(.+)/i)
  if (vsMatch) {
    // The query contains "vs" or "versus"
    // Split the entire query by vs/versus to get all app names
    const apps = normalizedQuery
      .split(/\s+(?:vs|versus)\s+/i)
      .map((s) => s.trim())
      .filter(Boolean)
    if (apps.length >= 2) {
      return apps
    }
  }

  return null
}

/**
 * Finds apps by name from the search data (fuzzy match)
 */
export function findAppsByNames(
  names: string[],
  comparisonApps: SearchIndexApp[],
): SearchIndexApp[] | null {
  const foundApps: SearchIndexApp[] = []

  for (const name of names) {
    const normalizedName = name.toLowerCase().trim()

    // Try exact match first
    let match = comparisonApps.find((app) => app.name.toLowerCase() === normalizedName)

    // Try slug match
    if (!match) {
      match = comparisonApps.find((app) => app.slug?.toLowerCase() === normalizedName)
    }

    // Try partial match (app name contains the search term)
    if (!match) {
      match = comparisonApps.find((app) => app.name.toLowerCase().includes(normalizedName))
    }

    // Try reverse partial match (search term contains app name)
    if (!match) {
      match = comparisonApps.find((app) => normalizedName.includes(app.name.toLowerCase()))
    }

    if (match) {
      foundApps.push(match)
    }
  }

  // Return null if we didn't find all apps
  if (foundApps.length !== names.length) {
    return null
  }

  return foundApps
}

/**
 * Validates that apps can be compared. Two apps overlap when they share at
 * least one category, AND at least one of the apps has that shared category
 * classified as primary. Secondary-only overlap isn't enough to justify a
 * comparison page.
 */
export function validateComparisonApps(apps: SearchIndexApp[]): boolean {
  if (apps.length < 2) return false

  // Get the intersection of categories across all apps
  const categoryArrays = apps.map((app) => new Set(app.categoryIds || []))

  // Find categories that ALL apps share
  const sharedCategories = categoryArrays.reduce((intersection, currentSet) => {
    return new Set([...intersection].filter((catId) => currentSet.has(catId)))
  })

  if (sharedCategories.size === 0) return false

  // Union of primary category IDs across all apps
  const primaryCategoryUnion = new Set<number>()
  for (const app of apps) {
    for (const catId of app.primaryCategoryIds || []) {
      primaryCategoryUnion.add(catId)
    }
  }

  // At least one shared category must be primary for at least one app
  return [...sharedCategories].some((catId) => primaryCategoryUnion.has(catId))
}

/**
 * Generates a comparison slug from app slugs
 * Format: "app1-vs-app2" or "app1-vs-app2-vs-app3"
 */
export function generateComparisonSlug(apps: SearchIndexApp[]): string {
  return apps.map((app) => app.slug || app.name.toLowerCase().replace(/\s+/g, '-')).join('-vs-')
}
